import { cn } from '@/lib/cn';
import { datadogRum } from '@datadog/browser-rum';
import { Button, type ButtonSize, type ButtonVariant } from '@heidi/design-system/button';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { Loader } from '@heidi/snowflake-ui/loader';
import { Mic } from 'lucide-react';
import { type ComponentProps, memo, type ReactNode, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { type DictationSource } from '@/components/constants';
import { DatadogDurationVital } from '@/components/constants/datadog-vitals';
import { DatadogDurationVitalStopper } from '@/components/utils/datadogDurationVitalStopper';
import { useIsSessionPageV2Enabled } from '@/features/flags';
import { useDictate } from '@/hooks/useDictate';
import { trackLegacyInlineDictationCompleted } from '@/lib/analytics/scribe/dictation';
import { isDictationAvailable } from '@/utils/dictateRecorderTransport';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { type Editor as TiptapEditor } from '@tiptap/react';

interface DictateControlsProps {
  disabled?: boolean;
  editor: TiptapEditor | null;
  source?: DictationSource;
  shouldShowCursor?: boolean;
  className?: string;
  isMobile?: boolean;
  size?: ButtonSize;
  variant?: ButtonVariant;
  onDictationSuccess?: () => void;
  tooltip?: {
    content: ReactNode;
    side?: ComponentProps<typeof TooltipContent>['side'];
  };
  /**
   * Document tab id. Threaded into `useDictate` so the per-editor recording
   * state is keyed identically to the writer side (the new-smart-dictation
   * auto-start keys on `{window}-document-{documentId}`). Without it the toolbar
   * mic reads `{window}-document`, never sees `isDictating`, and can't stop.
   */
  documentId?: string;
}

const DictateControls = ({
  disabled,
  editor,
  source,
  shouldShowCursor,
  className,
  isMobile,
  size,
  variant = 'ghost',
  tooltip,
  documentId,
  onDictationSuccess,
}: DictateControlsProps) => {
  const isSessionPageV2Enabled = useIsSessionPageV2Enabled();
  const sessionId = useCurrentSessionId();
  const {
    startDictatingTooltipText,
    isDictating,
    isLanguageSupported,
    isProcessing,
    handleStartDictation,
    handleStopDictation,
    dictationMode,
    startedAt,
  } = useDictate({
    editor,
    source,
    shouldShowCursor,
    documentId,
    onDictationSuccess,
  });

  const handleClick = useCallback(() => {
    if (isDictating) {
      const holdDurationMs = startedAt ? Date.now() - startedAt.getTime() : 0;
      trackLegacyInlineDictationCompleted({
        hold_duration_ms: holdDurationMs,
        triggered_by: 'button_click_scribe',
        source,
      });
      handleStopDictation();
      datadogRum.startDurationVital(DatadogDurationVital.STOP_DICTATION);
    } else {
      datadogRum.startDurationVital(DatadogDurationVital.START_DICTATION);
      handleStartDictation(dictationMode);
    }
  }, [
    isDictating,
    startedAt,
    source,
    handleStopDictation,
    handleStartDictation,
    dictationMode,
  ]);

  if (!isDictationAvailable(sessionId)) {
    return null;
  }

  // No wrapping DOM node: ButtonGroup's `>*` join selectors (radius + the
  // first-child start-border drop that leaves the 1px seam) only reach this
  // Button when it is a TooltipTrigger-asChild of the group.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant={variant}
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          // A running dictation must always be stoppable — `disabled`
          // (start-eligibility, e.g. generation in progress) never blocks the
          // stop action while actively dictating.
          disabled={(disabled && !isDictating) || isProcessing}
          size={size ?? (isMobile ? 'lg' : source === 'askAi' ? 'sm' : 'xs')}
          iconOnly
          className={cn('group', className)}
        >
          {isDictating ? (
            <>
              <DatadogDurationVitalStopper
                vitalName={DatadogDurationVital.START_DICTATION}
              />
              <div
                className={cn(
                  'hidden size-3 shrink-0 rounded-xs bg-validation-destructive text-validation-destructive group-hover:block'
                )}
              />

              <Mic className="shrink-0 text-validation-destructive group-hover:hidden" />
            </>
          ) : (
            <>
              <DatadogDurationVitalStopper
                vitalName={DatadogDurationVital.STOP_DICTATION}
              />

              {isProcessing ? (
                <Loader />
              ) : (
                <Mic
                  className={cn(
                    'shrink-0',
                    isSessionPageV2Enabled ? 'text-text-primary' : 'text-text-secondary'
                  )}
                />
              )}
            </>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side={tooltip?.side}>
        {!isLanguageSupported ? (
          <FormattedMessage
            defaultMessage="Dictation not available in this language"
            id="DGCCOzEVtt"
            description="Tooltip shown when dictation does not support the selected language"
          />
        ) : isDictating ? (
          <FormattedMessage
            defaultMessage="End dictating"
            id="g5hJjsrNvw"
            description="Tooltip action for stopping an active dictation"
          />
        ) : (
          (tooltip?.content ?? <span>{startDictatingTooltipText}</span>)
        )}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
};

export default memo(DictateControls);
